import React, { useEffect, useState } from 'react';
import TagFilter from '@Shared.Angular/flowingly.components/TagFilter/TagFilter';
import FlowModelSummary, {
  IFlowModelSummary
} from '@Client/runner.shared/FlowModelList/FlowModelSummary';
import { Guid } from '@Shared.Angular/@types/guid';
import { ITreeCategory } from '@Client/runner.services/runner.category-navigation.service';
import IReportFlowModelDetail from '@Shared.Angular/@types/core/contracts/queryModel/reports/reportFlowModelDetail';
import { getService } from '@Client/runner.hooks/useService';
import Category from '@Client/runner.directives/common/Category/Category';
import Search from '@Shared.Angular/flowingly.components/Search/Search';

const WorkflowReportsTab = () => {
  const pubsubService = getService('pubsubService');
  const flowListManager = getService('flowListManager');
  const runnerCategoryNavigationService = getService(
    'runnerCategoryNavigationService'
  );
  const appInsightsService = getService('appInsightsService');

  const [categories, setCategories] = useState<
    ITreeCategory<IReportFlowModelDetail>[]
  >([]);
  const [selectedCategory, setSelectedCategory] =
    useState<ITreeCategory<IReportFlowModelDetail> | null>(null);
  const [areCategoriesLoaded, setAreCategoriesLoaded] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [relevantTagIds, setRelevantTagIds] = useState<Guid[]>([]);
  const [tagFilterIds, setTagFilterIds] = useState<Guid[]>([]);
  const allCategoriesId = 'all-categories';
  const subscriberId = 'Reports';
  const reportsLoadedMetricName = 'workflowReportsListLoaded';

  useEffect(() => {
    pubsubService.subscribe(
      'SIGNALR_WORKFLOW_NAME_CHANGED',
      onFlowModelNameChanged,
      subscriberId
    );
    pubsubService.subscribe(
      'SIGNALR_SETUP_CATEGORY_DELETED',
      refreshFlows,
      subscriberId
    );

    if (
      !areCategoriesLoaded &&
      categories.length === 0 &&
      flowListManager.reportFlowModels.length === 0
    ) {
      refreshFlows();
    } else {
      setCategoriesFromListManager();
    }

    return () => {
      pubsubService.unsubscribeAll(subscriberId);
    };
  }, []);

  const onFlowModelNameChanged = (msg, data) => {
    const msgdata = JSON.parse(data);
    flowListManager.updateFlowNames(msgdata);
    setCategoriesFromListManager();
  };

  const refreshFlows = () => {
    appInsightsService.startEventTimer(reportsLoadedMetricName);
    return flowListManager.refreshReportFlowModels(false).then(() => {
      setCategoriesFromListManager();
      setAreCategoriesLoaded(true);
      appInsightsService.trackMetricIfTimerExist(reportsLoadedMetricName, {
        reportCount: flowListManager.reportFlowModels.length
      });
    });
  };

  const selectCategory = (categoryId: string) => {
    const newSelectedCategory =
      runnerCategoryNavigationService.findNodeById<IReportFlowModelDetail>(
        categories,
        categoryId
      );
    setSelectedCategory(newSelectedCategory);
    const relevantTagIds =
      newSelectedCategory?.flowModels.flatMap(
        (flowModel) => flowModel.parsedTags?.map((tag) => tag.id) || []
      ) || [];
    setRelevantTagIds(relevantTagIds);
  };

  const setCategoriesFromListManager = () => {
    const formattedCategories =
      runnerCategoryNavigationService.getFormattedReportTreeData(
        flowListManager.reportFlowModels
      );
    setCategories(addAllReportsCategory(formattedCategories));
  };

  useEffect(() => {
    if (categories?.length > 0 && selectedCategory === null) {
      selectCategory(allCategoriesId);
    }
  }, [categories, selectedCategory]);

  const addAllReportsCategory = (
    categories: ITreeCategory<IReportFlowModelDetail>[]
  ) => {
    if (
      !categories ||
      categories.find((category) => category.id === allCategoriesId)
    ) {
      return categories;
    }
    const copyOfAllFlows =
      runnerCategoryNavigationService.getFlowModelsFromNestedCategories(
        categories
      );
    return [
      {
        id: allCategoriesId,
        text: 'All',
        flowModels: copyOfAllFlows,
        items: []
      },
      ...categories
    ];
  };

  const onWorkflowReportClicked = (flowModelId: Guid) => {
    pubsubService.publish('WORKFLOW_REPORT_CLICKED', flowModelId);
  };

  const onSearchTextChanged = (value: string) => {
    setSearchText(value.toLowerCase());
  };

  const onTagFilterApplied = (selectedTags: Guid[]) => {
    setTagFilterIds(selectedTags);
  };

  return (
    <div className="workflow-reports-tab">
      <Category
        categorisedItems={categories}
        selectedCategoryId={selectedCategory?.id}
        onCategoryClick={selectCategory}
        isMobile={false}
      />
      <div className="workflow-reports">
        <div className="workflow-reports-toolbar">
          <Search
            placeholder="Search by flow model name or description"
            value={searchText}
            onValueChange={onSearchTextChanged}
          />
          <TagFilter usedTagIds={relevantTagIds} onApply={onTagFilterApplied} />
        </div>
        {selectedCategory &&
          selectedCategory.flowModels
            .filter((flowModel) => {
              return (
                (searchText === '' ||
                  flowModel.flowModelName.toLowerCase().includes(searchText) ||
                  flowModel.description?.toLowerCase().includes(searchText)) &&
                (tagFilterIds.length === 0 ||
                  flowModel.parsedTags?.some((tag) =>
                    tagFilterIds.includes(tag.id)
                  ))
              );
            })
            .map((flowModel) => {
              const summary: IFlowModelSummary = {
                id: flowModel.id,
                name: flowModel.flowModelName,
                description: flowModel.description,
                category: flowModel.category,
                processOwnerName: flowModel.processOwnerName || 'N/A',
                parsedTags: flowModel.parsedTags || [],
                isDeleted: !flowModel.active
              };
              return (
                <FlowModelSummary
                  key={summary.id}
                  isMobile={false}
                  flowModelSummary={summary}
                  onFlowModelClick={onWorkflowReportClicked}
                  showChips={true}
                />
              );
            })}
      </div>
    </div>
  );
};

export default WorkflowReportsTab;
